import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import { Checkbox } from '../../../../components/ui/Checkbox';
import Button from '../../../../components/ui/Button';

const NotificationSettingsSection = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [notifications, setNotifications] = useState({
    mentorResponses: true,
    courseRecommendations: true,
    interviewReminders: true,
    weeklyProgress: false,
    newFeatures: true,
    promotionalEmails: false
  });

  const notificationOptions = [
    {
      id: 'mentorResponses',
      label: 'Mentor Responses',
      description: 'Get notified when mentors reply to your consultation requests',
      icon: 'MessageSquare'
    },
    {
      id: 'courseRecommendations',
      label: 'Course Recommendations',
      description: 'Receive personalized course suggestions based on your performance',
      icon: 'BookOpen'
    },
    {
      id: 'interviewReminders',
      label: 'Interview Reminders',
      description: 'Reminders for scheduled practice sessions and upcoming interviews',
      icon: 'Bell'
    },
    {
      id: 'weeklyProgress',
      label: 'Weekly Progress Reports',
      description: 'Summary of your interview practice and improvement metrics',
      icon: 'TrendingUp'
    },
    {
      id: 'newFeatures',
      label: 'New Features & Updates',
      description: 'Stay informed about platform improvements and new capabilities',
      icon: 'Sparkles'
    },
    {
      id: 'promotionalEmails',
      label: 'Promotional Emails',
      description: 'Special offers, discounts, and premium feature announcements',
      icon: 'Tag'
    }
  ];

  const handleToggle = (id) => {
    setNotifications(prev => ({ ...prev, [id]: !prev?.[id] }));
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleToggleAll = (enabled) => {
    const updatedNotifications = {};
    Object.keys(notifications)?.forEach(key => {
      updatedNotifications[key] = enabled;
    });
    setNotifications(updatedNotifications);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={20} color="var(--color-accent)" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-semibold text-foreground">
              Notification Settings
            </h2>
            <p className="text-sm text-muted-foreground">
              Control your email and alert preferences
            </p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="p-6 space-y-6">
        {isEditing && (
          <div className="flex gap-2 pb-4 border-b border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleToggleAll(true)}
            >
              Enable All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleToggleAll(false)}
            >
              Disable All
            </Button>
          </div>
        )}

        <div className="space-y-4">
          {notificationOptions?.map((option) => (
            <div
              key={option?.id}
              className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors duration-150 ease-out"
            >
              <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <Icon name={option?.icon} size={20} color="var(--color-muted-foreground)" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-foreground mb-1">
                      {option?.label}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {option?.description}
                    </p>
                  </div>
                  <Checkbox
                    checked={notifications?.[option?.id]}
                    onChange={(e) => handleToggle(option?.id)}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {isEditing && (
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Check"
              iconPosition="left"
            >
              Save Settings
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSettingsSection;